import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Navigate, Route, Routes, useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Archive, ChatsCircle, ClipboardText, Handshake, Notebook, Scales, SquaresFour, Stack, UsersThree, Warning,
} from "@phosphor-icons/react";
import { lies } from "../../api/client";
import type { Projekt } from "../../api/types";
import { Meldung } from "../../components/Bausteine";
import { SuchFeld, SuchTreffer, useSuche } from "../../components/Suche";
import { usePfad } from "../../lib/pfad";
import { AnzeigenReiter } from "./AnzeigenReiter";
import { BegehungenReiter } from "./BegehungenReiter";
import { BesprechungenReiter } from "./BesprechungenReiter";
import { BeteiligteReiter } from "./BeteiligteReiter";
import { LeistungenReiter } from "./LeistungenReiter";
import { MaengelReiter } from "./MaengelReiter";
import { SchreibenReiter } from "./SchreibenReiter";
import { TagebuchReiter } from "./TagebuchReiter";
import { TeamReiter } from "./TeamReiter";
import { UebersichtReiter } from "./UebersichtReiter";

/** `id` ist, was in der Adresse steht: Kurzname oder (alte Links) die UUID. */
export function useProjekt(id: string | undefined) {
  return useQuery({ queryKey: ["projekt", id], queryFn: () => lies<Projekt>(`/projekte/${id}`), enabled: !!id });
}

/** Reihenfolge = Häufigkeit auf der Baustelle: nach der Übersicht Protokoll und Mängel. */
const REITER = [
  { pfad: "uebersicht", titel: "Übersicht", Icon: SquaresFour },
  { pfad: "besprechungen", titel: "Besprechungen", Icon: ChatsCircle },
  { pfad: "maengel", titel: "Mängel", Icon: Warning },
  { pfad: "begehungen", titel: "Abnahmen & Begehungen", Icon: ClipboardText },
  { pfad: "tagebuch", titel: "Bautagebuch", Icon: Notebook },
  { pfad: "anzeigen", titel: "Anzeigen & Nachträge", Icon: Scales },
  { pfad: "leistungen", titel: "Leistungen", Icon: Stack },
  { pfad: "beteiligte", titel: "Beteiligte", Icon: Handshake },
  { pfad: "team", titel: "Zugang", Icon: UsersThree, nurLeitung: true },
  { pfad: "schreiben", titel: "Ausgang", Icon: Archive },
];

export function ProjektSeite() {
  const { projektId = "" } = useParams();
  const projekt = useProjekt(projektId);
  // Absolut, nicht useResolvedPath(""): in der Splat-Route `/p/:id/*` loest das den
  // GANZEN aktuellen Pfad samt Reiter auf, und jeder Klick haengte seinen Reiter an.
  const basis = `/p/${projektId}`;
  const ort = useLocation().pathname;
  const nav = useNavigate();
  const p = projekt.data;
  // Alte Links und Verweise aus Protokoll/Begehung tragen die UUID: einmal auf den
  // Kurznamen umschreiben, der Reiter bleibt erhalten.
  useEffect(() => {
    if (p && projektId !== p.kurz) nav(ort.replace(basis, `/p/${p.kurz}`), { replace: true });
  }, [p, projektId, ort, basis, nav]);
  const reiter = REITER.find((r) => ort.startsWith(`${basis}/${r.pfad}`));
  usePfad(p ? [{ titel: p.name, nach: `${basis}/besprechungen` }, { titel: reiter?.titel ?? "" }] : []);
  const suche = useSuche(p?.id ?? "");

  if (projekt.error) return <main className="seite"><Meldung fehler={projekt.error} /></main>;
  if (!p) return null;
  const kann = p.meine_rolle !== "lesen";

  return (
    <main className="seite">
      <div className="seite-kopf">
        <div>
          {p.nummer && <div className="mute">{p.nummer}</div>}
          <h1>{p.name}</h1>
          <div className="dim">{[p.ort, p.bauherr && `Bauherr ${p.bauherr}`].filter(Boolean).join(" · ")}</div>
        </div>
        <div className="luft" />
        <SuchFeld q={suche.q} setQ={suche.setQ} />
      </div>
      <SuchTreffer s={suche} />
      <nav className="reiter">
        {REITER.filter((r) => !r.nurLeitung || p.meine_rolle === "leitung").map(({ pfad, titel, Icon }) => (
          <button key={pfad} className={ort.startsWith(`${basis}/${pfad}`) ? "aktiv" : ""}
                  onClick={() => nav(`${basis}/${pfad}`)}>
            <Icon size={17} /> {titel}
          </button>
        ))}
      </nav>
      <Routes>
        <Route index element={<Navigate to={`${basis}/uebersicht`} replace />} />
        <Route path="uebersicht" element={<UebersichtReiter projekt={p} />} />
        <Route path="besprechungen" element={<BesprechungenReiter projekt={p} kann={kann} />} />
        <Route path="maengel" element={<MaengelReiter projekt={p} kann={kann} />} />
        <Route path="begehungen" element={<BegehungenReiter projekt={p} kann={kann} />} />
        <Route path="tagebuch" element={<TagebuchReiter projekt={p} kann={kann} />} />
        <Route path="anzeigen" element={<AnzeigenReiter projekt={p} kann={kann} />} />
        <Route path="leistungen" element={<LeistungenReiter projekt={p} kann={kann} />} />
        <Route path="beteiligte" element={<BeteiligteReiter projekt={p} kann={kann} />} />
        {/* Nur die Leitung — per Adresse kam sonst auch die Bearbeitung hin und lief in 403er. */}
        <Route path="team" element={p.meine_rolle === "leitung" ? <TeamReiter projekt={p} /> : <Navigate to={`${basis}/besprechungen`} replace />} />
        <Route path="schreiben" element={<SchreibenReiter projekt={p} />} />
      </Routes>
    </main>
  );
}
